import { useAlertContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { clientVolunteerAssignmentsApi, volunteersApi } from "@/api";
import { withLocalErrorHandling } from "@/api/middleware/errorMiddleware";
import { QueryKey } from "@/query/Query";
import { useTranslation } from "@/translations";

/**
 * Manages the client's volunteer assignment (Ehrenamtliche Zuweisung): reads the currently
 * assigned volunteer, offers the volunteer list for the release dropdown, and wraps the
 * assign/revoke actions. Both actions write a case note (Aktenvermerk) on the backend, so
 * successes invalidate the case-notes list alongside the assignment.
 */
export function useVolunteerAssignment(clientId: string) {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const queryClient = useQueryClient();

  const {
    data: assignment,
    isPending,
    isError,
  } = useQuery({
    queryKey: [QueryKey.ClientVolunteerAssignment, clientId],
    queryFn: () => clientVolunteerAssignmentsApi.getClientVolunteerAssignment({ clientId }),
  });

  const { data: volunteerOptions = [] } = useQuery({
    queryKey: [QueryKey.Volunteers],
    queryFn: () => volunteersApi.searchVolunteers({}),
    select: (results) => results.data,
  });

  const invalidateAssignmentQueries = () => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientVolunteerAssignment, clientId] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.ClientCaseNotes, clientId] });
  };

  const assign = useMutation({
    // onError toasts every failure itself; without this the global middleware would toast too.
    mutationFn: (volunteerId: string) =>
      clientVolunteerAssignmentsApi.assignVolunteerToClient(
        { clientId, assignVolunteerRequest: { volunteerId } },
        withLocalErrorHandling
      ),
    onSuccess: () => {
      invalidateAssignmentQueries();
      showSuccess(t("volunteerAssignment.assignSuccess"));
    },
    onError: () => showError(t("volunteerAssignment.assignError")),
  });

  const revoke = useMutation({
    mutationFn: () => clientVolunteerAssignmentsApi.removeVolunteerAssignment({ clientId }, withLocalErrorHandling),
    onSuccess: () => {
      invalidateAssignmentQueries();
      showSuccess(t("volunteerAssignment.revokeSuccess"));
    },
    onError: () => showError(t("volunteerAssignment.revokeError")),
  });

  return { assignment, isPending, isError, volunteerOptions, assign, revoke };
}
